// // frontend/src/pages/PharmacistLandingPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import Background from '../components/Background/Background';
import '../styles/PharmacistLandingPage.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

// ---------- Types (DB-backed) ----------
type WorkOrderStatus =
  | 'PENDING'
  | 'PARTIALLY_APPROVED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'COMPLETED_EXTERNALLY';
type WorkOrderType = 'CHANGE' | 'REGISTRATION' | 'DISPENSING_STATUS';

type QueueItem = {
  itemId: string;
  action: string; // ADD/EDIT/REMOVE/PAUSE/RESUME
  medicationIdentifier: string;
  proposedPayload: any;
  decision: 'APPROVE' | 'DENY' | null;
  reasonCode: string | null;
  reviewedAt: string | null;
};

type QueueWorkOrder = {
  work_order_id: string;
  order_type: WorkOrderType;
  patient_id: string;
  ssn: string | null;
  pharmacy_id: string;
  status: WorkOrderStatus;
  created_at: string; // ISO
  first_displayed_to_pharmacy_at?: string | null;
  fully_processed_at?: string | null;
  first_rejected_at?: string | null;
  last_resubmitted_at?: string | null;
  items: QueueItem[];
};

// ---------- UI shape (your existing list expects these) ----------
type ReviewStatus = 'pending' | 'reviewed' | 'archived';

type ChangeEntry = {
  id: string; // workOrderId
  patientId: string; // patient uuid
  ssn: string;
  title: string; // display label
  dateLabel: string; // like "2026-01-25 | 14:30"
  requiresExtraRoll: boolean;
  requiresReschedule: boolean;
  reviewStatus: ReviewStatus;
  sortKey: number;
  orderType: WorkOrderType;
};

function toDateLabel(iso: string) {
  const d = new Date(iso);
  // Icelandic locale formatting; tweak if you prefer
  const date = d.toLocaleDateString('is-IS');
  const time = d.toLocaleTimeString('is-IS', { hour: '2-digit', minute: '2-digit' });
  return `${date} | ${time}`;
}

function computeFlags(order: QueueWorkOrder) {
  // This mimics your old heuristic:
  // - if any EDIT/REMOVE => requiresReschedule
  // - else if any ADD => requiresExtraRoll
  // - DISPENSING_STATUS always neither (or make its own pill)
  if (order.order_type === 'DISPENSING_STATUS') {
    return { requiresReschedule: false, requiresExtraRoll: false };
  }

  const actions = order.items.map((i) => i.action);
  const requiresReschedule = actions.includes('EDIT') || actions.includes('REMOVE');
  const requiresExtraRoll = !requiresReschedule && actions.includes('ADD');
  return { requiresReschedule, requiresExtraRoll };
}

function computeReviewStatus(order: QueueWorkOrder): ReviewStatus {
  // For landing page:
  // - PENDING / PARTIALLY_APPROVED => pending
  // - APPROVED / REJECTED => reviewed (processed)
  // - archived doesn't exist in DB now; "old changes" can be derived from fully_processed_at != null
  if (order.status === 'PENDING' || order.status === 'PARTIALLY_APPROVED') return 'pending';
  if (order.status === 'APPROVED' || order.status === 'REJECTED') return 'reviewed';
  return 'reviewed';
}

function titleFor(order: QueueWorkOrder): string {
  if (order.order_type === 'DISPENSING_STATUS') {
    const first = order.items[0]?.action;
    if (first === 'PAUSE') return 'Stöðva skömmtun';
    if (first === 'RESUME') return 'Endurvekja skömmtun';
    return 'Breyting á stöðu skömmtunar';
  }
  if (order.order_type === 'REGISTRATION') return 'Nýskráning sjúklings';
  return 'Breyting á skömmtun';
}

const PharmacistLandingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'new-changes' | 'old-changes'>('new-changes');
  const [rows, setRows] = useState<ChangeEntry[]>([]);
  const [processedRows, setProcessedRows] = useState<ChangeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showProcessed, setShowProcessed] = useState(false);
  const [pharmacyName, setPharmacyName] = useState<string>('');

  const API_BASE = 'https://api-staging.mitoflux.is';
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(getAccessTokenSilently, `${API_BASE}/api/pharmacy/me`);
        if (!res.ok) return;

        const data = await res.json();
        setPharmacyName(data.pharmacyName);
      } catch (e) {
        console.error('Failed to load pharmacy name', e);
      }
    })();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await fetchWithAuth(
          getAccessTokenSilently,
          `${API_BASE}/api/pharmacy/queue/details`
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`API ${res.status}: ${text}`);
        }

        const data = (await res.json()) as { rows: QueueWorkOrder[] };
        const mapped: ChangeEntry[] = (data.rows ?? []).map((wo) => {
          const { requiresExtraRoll, requiresReschedule } = computeFlags(wo);
          const sortKey = new Date(wo.created_at).getTime();

          return {
            id: wo.work_order_id,
            patientId: wo.patient_id,
            ssn: wo.ssn ?? '—',
            title: titleFor(wo),
            dateLabel: toDateLabel(wo.created_at),
            requiresExtraRoll,
            requiresReschedule,
            reviewStatus: computeReviewStatus(wo),
            sortKey,
            orderType: wo.order_type,
          };
        });

        mapped.sort((a, b) => a.sortKey - b.sortKey);
        setRows(mapped);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || 'Villa kom upp');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAccessTokenSilently]);

  const { openEntries, processedEntries } = useMemo(() => {
    // const open = rows.filter((r) => r.reviewStatus !== 'archived' && r.reviewStatus !== 'reviewed');
    // const processed = rows.filter((r) => r.reviewStatus === 'reviewed');

    const open = rows.filter((r) => r.reviewStatus === 'pending');
    const processed = rows.filter((r) => r.reviewStatus === 'reviewed');

    return { openEntries: open, processedEntries: processed };
  }, [rows]);

  const toggleProcessed = async () => {
    const next = !showProcessed;
    setShowProcessed(next);

    if (next && processedRows.length === 0) {
      try {
        const res = await fetchWithAuth(
          getAccessTokenSilently,
          `${API_BASE}/api/pharmacy/work-orders/finished`
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Finished API ${res.status}: ${text}`);
        }

        const data = (await res.json()) as Array<{
          workOrderId: string;
          patientId: string;
          ssn: string | null;
          status: 'APPROVED' | 'REJECTED' | 'PARTIALLY_APPROVED';
          orderType: WorkOrderType;
          createdAt: string;
          fullyProcessedAt: string | null;
        }>;

        const mapped: ChangeEntry[] = data.map((wo) => ({
          id: wo.workOrderId,
          patientId: wo.patientId,
          ssn: wo.ssn ?? '—',
          title:
            wo.status === 'REJECTED' || wo.status === 'PARTIALLY_APPROVED' ? 'Hafnað' : 'Samþykkt',
          dateLabel: toDateLabel(wo.fullyProcessedAt ?? wo.createdAt),
          requiresExtraRoll: false,
          requiresReschedule: false,
          reviewStatus: 'reviewed',
          sortKey: new Date(wo.fullyProcessedAt ?? wo.createdAt).getTime(),
          orderType: wo.orderType,
        }));

        mapped.sort((a, b) => b.sortKey - a.sortKey);
        setProcessedRows(mapped);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || 'Villa kom upp');
      }
    }
  };

  const content = useMemo(() => {
    if (loading) return <div>Hleð breytingum…</div>;
    if (error) return <div className="error">{error}</div>;
    // if (rows.length === 0) return <div>Engar breytingar skráðar.</div>;

    return (
      <>
        <div className="change-labels one-list">
          <span className="change-label-empty" />
          <span className="change-label-ssn">Tegund</span>
          <span className="change-label-time">Tími innsendingar</span>
        </div>

        <div className="change-list one-list">
          {openEntries.length === 0 ? (
            <div>Engar breytingar skráðar.</div>
          ) : (
            <>
              <div className="change-list">
                {openEntries.map((entry, idx) => (
                  <div
                    className="change-entry"
                    key={`${entry.id}-${entry.sortKey}-${idx}`}
                    onClick={() =>
                      navigate(`/pharmacist/work-order/${encodeURIComponent(entry.id)}`)
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      navigate(`/pharmacist/work-order/${encodeURIComponent(entry.id)}`)
                    }
                  >
                    <span className="change-name">
                      Kennitala: {entry.ssn}
                      <span className="pill-box">
                        {entry.requiresExtraRoll && (
                          <span className="pill pill-extra">Krefst aukarúllu</span>
                        )}
                        {entry.requiresReschedule && (
                          <span className="pill pill-resched">Krefst endurskömmtunar</span>
                        )}
                        {entry.orderType === 'DISPENSING_STATUS' && (
                          <span className="pill pill-processed">Stöðubreyting</span>
                        )}
                      </span>
                    </span>

                    <span className="change-ssn">{entry.title}</span>
                    <span className="change-time">{entry.dateLabel}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {/* {openEntries.map((entry, idx) => (
            <div
              className="change-entry"
              key={`${entry.id}-${entry.sortKey}-${idx}`}
              onClick={() => navigate(`/pharmacist/work-order/${encodeURIComponent(entry.id)}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                navigate(`/pharmacist/work-order/${encodeURIComponent(entry.id)}`)
              }
            >
              <span className="change-name">
                Kennitala: {entry.ssn}
                <span className="pill-box">
                  {entry.requiresExtraRoll && (
                    <span className="pill pill-extra">Krefst aukarúllu</span>
                  )}
                  {entry.requiresReschedule && (
                    <span className="pill pill-resched">Krefst endurskömmtunar</span>
                  )}
                  {entry.orderType === 'DISPENSING_STATUS' && (
                    <span className="pill pill-processed">Stöðubreyting</span>
                  )}
                </span>
              </span>

              <span className="change-ssn">{entry.title}</span>
              <span className="change-time">{entry.dateLabel}</span>
            </div>
          ))} */}
        </div>

        <div className="processed-toggle" onClick={toggleProcessed}>
          <span className="processed-toggle-left">
            <span>Afgreiddar breytingar</span>
            <span className={`toggle-arrow ${showProcessed ? 'open' : ''}`}>▶</span>
          </span>
        </div>

        {showProcessed && processedRows.length > 0 && (
          <div className="change-list processed-list">
            {processedRows.map((entry, idx) => (
              <div
                className="change-entry archived"
                key={`processed-${entry.id}-${idx}`}
                onClick={() => navigate(`/pharmacist/finished/${encodeURIComponent(entry.id)}`)}
                role="button"
                tabIndex={0}
              >
                <span className="change-name">
                  Kennitala: {entry.ssn}
                  <span className="pill-box">
                    <span className="pill pill-processed">Afgreitt</span>
                  </span>
                </span>
                <span className="change-ssn">{entry.title}</span>
                <span className="change-time">{entry.dateLabel}</span>
              </div>
            ))}
          </div>
        )}

        {showProcessed && processedRows.length === 0 && (
          <div className="no-processed">Engar afgreiddar breytingar ennþá.</div>
        )}
      </>
    );
  }, [rows, openEntries, processedRows, loading, error, navigate, showProcessed]);

  return (
    <>
      <Header />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'new-changes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('new-changes');
            navigate('/pharmacist');
          }}
        >
          Nýjar breytingar á skömmtunum
        </button>
        <button
          className={`tab ${activeTab === 'old-changes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('old-changes');
            navigate('/pharmacist/finished');
          }}
        >
          Fyrri breytingar
        </button>
      </div>

      <Background>
        <div className="pharmacy-page-content">
          <h2 className="section-title">Óafgreiddar breytingar á skömmtunum</h2>
          <p className="pharmacy-name">{pharmacyName || '—'}</p>
          <hr className="divider" />
          {content}
        </div>
      </Background>
    </>
  );
};

export default PharmacistLandingPage;
